/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import awsConfig from '../../Configuration/environment-config';
import { formatDateToYYMMDDFormat, getLoginTokenHeaderWhenReady, signedFetch } from '../Utilities/utils';
import { v4 as uuidv4 } from 'uuid';

export enum UserType {
  Customer = 'Customer',
  Vendor = 'Vendor',
}

export enum Message {
  NoEndPoint = 'Endpoint not found',
}

// Headers for the analytics REST calls. The analytics service reads the logged-in user from userid and the
// session from logintoken (the same header signedFetch sends); each is only sent when there is a value.
const getAnalyticsHeaders = async (userId?: string): Promise<Record<string, string>> => ({
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'X-PZY-Client-Type': 'Web',
  ...(userId ? { userid: userId } : {}),
  ...(await getLoginTokenHeaderWhenReady()),
});

// taskSummary can send its body gzipped, as base64 text in { encoding, data }: it passes API Gateway as plain JSON
// and is unzipped here by the browser (several MB of map data then downloads as a few hundred KB). Only asked for
// when the browser has DecompressionStream; older backends ignore the parameter and send the JSON as it is.
const GZIP_BASE64 = 'gzip-base64';
const canUnzipResponses = typeof DecompressionStream !== 'undefined';

const unzipGzipBase64 = async (base64: string): Promise<unknown> => {
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
  const unzipped = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'));
  return new Response(unzipped).json();
};

export default class AnalyticsApiService {
  static async taskSummary(id: string, workDate: string | number | Date, userType: UserType, userId: string) {
    try {
      const taskSummary = awsConfig.aws_cloud_logic_custom.find(
        (apiEndpoint: { name: string }) => apiEndpoint.name === 'taskSummary'
      );

      if (!taskSummary?.endpoint) {
        return { data: [], errors: [{ message: Message.NoEndPoint }] };
      }

      const requestOptions = {
        method: 'GET',
        headers: await getAnalyticsHeaders(userId),
      };

      const startDateYMDFormat = formatDateToYYMMDDFormat(workDate);
      const queryParams =
        userType === UserType.Customer
          ? `?workDate=${startDateYMDFormat}&customerId=${id}&userId=${userId}`
          : `?workDate=${startDateYMDFormat}&vendorId=${id}&userId=${userId}`;
      const responseEncodingParam = canUnzipResponses ? `&responseEncoding=${GZIP_BASE64}` : '';
      const fetchSummary = async (encodingParam: string) => {
        const taskSummaryResponse = await fetch(
          `${taskSummary.endpoint}${queryParams}${encodingParam}`,
          requestOptions
        );
        // A failed request (403, 5xx, gateway timeout) is an error, not a summary with no facilities in it.
        if (!taskSummaryResponse.ok) throw new Error(`taskSummary failed: HTTP ${taskSummaryResponse.status}`);
        const responseJson = await taskSummaryResponse.json();
        return responseJson?.encoding === GZIP_BASE64 ? unzipGzipBase64(responseJson.data) : responseJson;
      };

      let summary: unknown;
      try {
        summary = await fetchSummary(responseEncodingParam);
      } catch (error) {
        // Asked for gzip and something on the way failed: ask once more for the plain response.
        if (!responseEncodingParam) throw error;
        summary = await fetchSummary('');
      }
      return { data: summary ?? null, errors: [] };
    } catch (e: any) {
      return { data: [], errors: e?.errors ?? [{ message: e?.message ?? 'taskSummary failed' }] };
    }
  }

  static async paginatedTaskSummary(
    id: string,
    fromDate: string | number | Date,
    toDate: string | number | Date,
    userType: UserType,
    userId: string,
    pageIndex: number,
    itemsPerPage: number,
    sortBy?: string,
    sortOrder?: string,
    filterQueryParams?: string,
    selectedUserId?: string
  ) {
    try {
      const paginatedTaskSummary = awsConfig.aws_cloud_logic_custom.find(
        (apiEndpoint: { name: string }) => apiEndpoint.name === 'paginatedTaskSummary'
      );

      if (!paginatedTaskSummary?.endpoint) {
        return { data: [], errors: [{ message: Message.NoEndPoint }] };
      }

      const requestOptions = {
        method: 'GET',
        headers: await getAnalyticsHeaders(userId),
      };

      const fromDateYMDFormat = fromDate ? formatDateToYYMMDDFormat(fromDate) : undefined;
      const toDateYMDFormat = toDate ? formatDateToYYMMDDFormat(toDate) : undefined;

      // eslint-disable-next-line sonarjs/no-nested-template-literals
      const queryParams = `?${userType === UserType.Customer ? `customerId=${id}` : `vendorId=${id}`}${selectedUserId ? `&userId=${selectedUserId}` : ''}&pageIndex=${pageIndex}&limit=${itemsPerPage}${sortBy ? `&sortBy=${sortBy}` : ''}${sortOrder ? `&sortOrder=${sortOrder}` : ''}${filterQueryParams ? '&' + filterQueryParams : ''}${fromDateYMDFormat ? `&fromDate=${fromDateYMDFormat}` : ''}${toDateYMDFormat ? `&toDate=${toDateYMDFormat}` : ''}`;

      const endpointWithParams = `${paginatedTaskSummary.endpoint}${queryParams}`;

      const taskSummaryResponse = await fetch(endpointWithParams, requestOptions);

      const responseJson = await taskSummaryResponse.json();

      return { data: responseJson ?? null, errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async oneTimeJobs(
    id: string,
    fromDate: string | number | Date,
    toDate: string | number | Date,
    userType: UserType,
    userId: string
  ) {
    try {
      const oneTimeJobs = awsConfig.aws_cloud_logic_custom.find(
        (apiEndpoint: { name: string }) => apiEndpoint.name === 'oneTimeJobs'
      );

      if (!oneTimeJobs?.endpoint) {
        return { data: [], errors: [{ message: Message.NoEndPoint }] };
      }

      const requestOptions = {
        method: 'GET',
        headers: await getAnalyticsHeaders(),
      };

      const fromDateYMDFormat = formatDateToYYMMDDFormat(fromDate);
      const toDateYMDFormat = formatDateToYYMMDDFormat(toDate);
      const queryParams =
        userType === UserType.Customer
          ? `?fromDate=${fromDateYMDFormat}&toDate=${toDateYMDFormat}&customerId=${id}&userId=${userId}`
          : `?fromDate=${fromDateYMDFormat}&toDate=${toDateYMDFormat}&vendorId=${id}&userId=${userId}`;
      const endpointWithParams = `${oneTimeJobs.endpoint}${queryParams}`;

      const oneTimeJobsResponse = await fetch(endpointWithParams, requestOptions);

      const responseJson = await oneTimeJobsResponse.json();

      return { data: responseJson ?? null, errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async noShowFacilities(
    id: string,
    fromDate: string | number | Date,
    toDate: string | number | Date,
    userType: UserType,
    userId: string
  ) {
    try {
      const noShowFacilities = awsConfig.aws_cloud_logic_custom.find(
        (apiEndpoint: { name: string }) => apiEndpoint.name === 'noShowFacilities'
      );

      if (!noShowFacilities?.endpoint) {
        return { data: [], errors: [{ message: Message.NoEndPoint }] };
      }

      const requestOptions = {
        method: 'GET',
        headers: await getAnalyticsHeaders(),
      };

      const fromDateYMDFormat = formatDateToYYMMDDFormat(fromDate);
      const toDateYMDFormat = formatDateToYYMMDDFormat(toDate);
      const queryParams =
        userType === UserType.Customer
          ? `?fromDate=${fromDateYMDFormat}&toDate=${toDateYMDFormat}&customerId=${id}&userId=${userId}`
          : `?fromDate=${fromDateYMDFormat}&toDate=${toDateYMDFormat}&vendorId=${id}&userId=${userId}`;
      const endpointWithParams = `${noShowFacilities.endpoint}${queryParams}`;

      const noShowFacilitiesResponse = await fetch(endpointWithParams, requestOptions);

      const responseJson = await noShowFacilitiesResponse.json();

      return { data: responseJson ?? null, errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async shiftTrend(
    id: string,
    fromDate: string | number | Date,
    toDate: string | number | Date,
    userType: UserType,
    userId: string,
    selectedUserId?: string,
    selectedFacilityId?: string,
    selectedRoleId?: string
  ) {
    try {
      const shiftTrend = awsConfig.aws_cloud_logic_custom.find(
        (apiEndpoint: { name: string }) => apiEndpoint.name === 'shiftTrend'
      );

      if (!shiftTrend?.endpoint) {
        return { data: [], errors: [{ message: Message.NoEndPoint }] };
      }

      const requestOptions = {
        method: 'GET',
        headers: await getAnalyticsHeaders(),
      };

      const fromDateYMDFormat = formatDateToYYMMDDFormat(fromDate);
      const toDateYMDFormat = formatDateToYYMMDDFormat(toDate);
      const queryParams =
        userType === UserType.Customer
          ? `?fromDate=${fromDateYMDFormat}&toDate=${toDateYMDFormat}&customerId=${id}&vendorId=${selectedRoleId}&facilityId=${selectedFacilityId}&userId=${userId}`
          : `?fromDate=${fromDateYMDFormat}&toDate=${toDateYMDFormat}&vendorId=${id}&customerId=${selectedRoleId}&userId=${selectedUserId}`;
      const endpointWithParams = `${shiftTrend.endpoint}${queryParams}`;

      const shiftTrendResponse = await fetch(endpointWithParams, requestOptions);

      const responseJson = await shiftTrendResponse.json();

      return { data: responseJson ?? null, errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async taskPerformance() {
    try {
      const taskPerformance = awsConfig.aws_cloud_logic_custom.find(
        (apiEndpoint: { name: string }) => apiEndpoint.name === 'taskPerformance'
      );

      if (!taskPerformance?.endpoint) {
        return { data: [], errors: [{ message: Message.NoEndPoint }] };
      }

      const requestOptions = {
        method: 'GET',
        headers: await getAnalyticsHeaders(),
      };

      const endpointWithParams = `${taskPerformance.endpoint}`;

      const taskPerformanceResponse = await fetch(endpointWithParams, requestOptions);

      const responseJson = await taskPerformanceResponse.json();

      return { data: responseJson ?? null, errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async inspectionByDays() {
    try {
      const inspectionByDays = awsConfig.aws_cloud_logic_custom.find(
        (apiEndpoint: { name: string }) => apiEndpoint.name === 'inspectionByDays'
      );

      if (!inspectionByDays?.endpoint) {
        return { data: [], errors: [{ message: Message.NoEndPoint }] };
      }

      const requestOptions = {
        method: 'GET',
        headers: await getAnalyticsHeaders(),
      };

      const endpointWithParams = `${inspectionByDays.endpoint}`;

      const inspectionByDaysResponse = await fetch(endpointWithParams, requestOptions);

      const responseJson = await inspectionByDaysResponse.json();

      return { data: responseJson ?? null, errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async inspectionByLocation() {
    try {
      const inspectionByLocation = awsConfig.aws_cloud_logic_custom.find(
        (apiEndpoint: { name: string }) => apiEndpoint.name === 'inspectionByLocation'
      );

      if (!inspectionByLocation?.endpoint) {
        return { data: [], errors: [{ message: Message.NoEndPoint }] };
      }

      const requestOptions = {
        method: 'GET',
        headers: await getAnalyticsHeaders(),
      };

      const endpointWithParams = `${inspectionByLocation.endpoint}`;

      const inspectionByLocationResponse = await fetch(endpointWithParams, requestOptions);

      const responseJson = await inspectionByLocationResponse.json();

      return { data: responseJson ?? null, errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async taskInspection() {
    try {
      const taskInspection = awsConfig.aws_cloud_logic_custom.find(
        (apiEndpoint: { name: string }) => apiEndpoint.name === 'taskInspection'
      );

      if (!taskInspection?.endpoint) {
        return { data: [], errors: [{ message: Message.NoEndPoint }] };
      }

      const requestOptions = {
        method: 'GET',
        headers: await getAnalyticsHeaders(),
      };

      const endpointWithParams = `${taskInspection.endpoint}`;

      const taskInspectionResponse = await fetch(endpointWithParams, requestOptions);

      const responseJson = await taskInspectionResponse.json();

      return { data: responseJson ?? null, errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async getFilterValues(userId: string) {
    try {
      const getFilterValues = awsConfig.aws_cloud_logic_custom.find(
        (apiEndpoint: { name: string }) => apiEndpoint.name === 'getFilterValues'
      );

      if (!getFilterValues?.endpoint) {
        return { data: [], errors: [{ message: Message.NoEndPoint }] };
      }

      const requestOptions = {
        method: 'GET',
        headers: await getAnalyticsHeaders(),
      };

      const endpointWithParams = `${getFilterValues.endpoint}?userId=${userId}`;

      const getFilterValuesResponse = await fetch(endpointWithParams, requestOptions);

      const responseJson = await getFilterValuesResponse.json();

      return { data: responseJson ?? null, errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async signedApprovalEndPoint(
    payload: {
      scWorkOrderId: string;
      hasSignedApproval: boolean;
    },
    userId?: string
  ) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'signedApproval');

      if (!endpointConfig?.endpoint) {
        console.error('Endpoint configuration not found');
        return { success: false, errors: [{ message: 'Endpoint not found' }] };
      }

      const endpointUrl = `${endpointConfig.endpoint}`;

      const response = await signedFetch({
        method: 'PATCH',
        url: endpointUrl,
        body: payload,
        requestId: uuidv4(),
        userId,
      });

      return {
        data: response?.data ?? {},
        errors: response?.errors ?? [],
      };
    } catch (e: any) {
      return { data: {}, errors: e?.errors || [{ message: 'Something went wrong' }] };
    }
  }
}
